import { canInstallLicenseForWorkspace, isSaasCommercialBuyer } from "@/lib/accountStatus";

/** Días antes del vencimiento (trial o licencia) en los que se muestran CTAs de solicitud/activación. */
export const LICENSE_RENEWAL_WINDOW_DAYS = 15;

/** Días antes del vencimiento en los que la barra «Datos de Licencia» del panel se abre sola. */
export const LICENSE_DATA_ACCORDION_OPEN_DAYS = 30;

/** @deprecated Usar LICENSE_RENEWAL_WINDOW_DAYS — alias para imports existentes. */
export const LICENSE_RENEWAL_REMINDER_DAYS = LICENSE_RENEWAL_WINDOW_DAYS;

/** Días hasta valid_to (YYYY-MM-DD); null si no aplica. Fechas a mediodía local para evitar bordes UTC. */
export function daysLeftUntilValidTo(validToStr) {
  if (!validToStr) return null;
  try {
    const endStr = String(validToStr).slice(0, 10);
    const d0 = new Date();
    const todayStr = `${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, "0")}-${String(d0.getDate()).padStart(2, "0")}`;
    const t = new Date(`${todayStr}T12:00:00`);
    const e = new Date(`${endStr}T12:00:00`);
    return Math.ceil((e.getTime() - t.getTime()) / 86400000);
  } catch {
    return null;
  }
}

export function isInLicenseRenewalWindow(
  validToStr,
  windowDays = LICENSE_RENEWAL_WINDOW_DAYS,
) {
  const daysLeft = daysLeftUntilValidTo(validToStr);
  return typeof daysLeft === "number" && daysLeft <= windowDays && daysLeft >= 0;
}

/**
 * Fecha de vencimiento efectiva desde GET /system/info.
 * Trial: subscription.trial_ends_at. Licencia instalada: license.payload.valid_to.
 * @param {object} [systemInfo]
 */
export function resolveLicenseExpirationDate(systemInfo = {}) {
  const sub = systemInfo.subscription || {};
  const lic = systemInfo.license || {};
  if (sub.mode === "trial" && sub.trial_ends_at) {
    return String(sub.trial_ends_at).slice(0, 10);
  }
  const validTo = lic.payload?.valid_to;
  if (validTo) return String(validTo).slice(0, 10);
  if (sub.trial_ends_at) return String(sub.trial_ends_at).slice(0, 10);
  return null;
}

/**
 * ¿Dentro de la ventana de renovación (≤ windowDays) o ya vencido por fecha?
 * @param {object} [systemInfo]
 * @param {number} [windowDays]
 */
export function isWithinLicenseRenewalWindow(
  systemInfo = {},
  windowDays = LICENSE_RENEWAL_WINDOW_DAYS,
) {
  const expiration = resolveLicenseExpirationDate(systemInfo);
  if (!expiration) return false;
  const daysLeft = daysLeftUntilValidTo(expiration);
  return typeof daysLeft === "number" && daysLeft <= windowDays;
}

/** Cuenta en flujo comprador trial (bootstrap, registro o post-alta). */
export function userInClientTrialFlow(user = {}) {
  return Boolean(
    user.trial_onboarding_completed_at ||
    user.needs_trial_registration ||
    user.trial_bootstrap
  );
}

/** Trial/licencia incómoda o dentro de ventana de renovación → flujo PDF/solicitud. */
export function subscriptionNeedsLicensePdfFlow(sub = {}, licSt = "", systemInfo = null) {
  if (!sub.ok) return true;
  if (systemInfo && isWithinLicenseRenewalWindow(systemInfo)) return true;
  const reason = sub.reason;
  if (reason === "trial_expired" || reason === "invalid_hardware" || reason === "expired") return true;
  if (licSt === "invalid_hardware" || licSt === "expired" || licSt === "missing") return true;
  return false;
}

export function subscriptionNeedsActivation(sub = {}, licSt = "", systemInfo = null) {
  if (!sub.ok) return true;
  if (systemInfo && isWithinLicenseRenewalWindow(systemInfo)) return true;
  if (sub.reason === "trial_expired") return true;
  if (licSt === "expired" || licSt === "invalid_hardware" || licSt === "invalid") return true;
  return false;
}

function subscriptionRequiresLicenseCta(systemInfo = {}) {
  const sub = systemInfo.subscription || {};
  const licSt = systemInfo.license?.status || "";

  // Trial activo: sin bloque «requiere activación» (CTA principal en barra de instalación).
  if (sub.mode === "trial" && sub.ok) return false;

  if (!sub.ok) return true;
  if (sub.reason === "trial_expired") return true;
  if (["expired", "invalid_hardware", "invalid_token"].includes(String(sub.reason || ""))) {
    return true;
  }
  if (["expired", "invalid_hardware", "invalid"].includes(licSt)) return true;
  if (sub.mode === "licensed" && licSt === "missing") return true;

  return isWithinLicenseRenewalWindow(systemInfo);
}

/** ¿Mostrar panel de activación dual (Stripe placeholder + PDF)? */
export function showLicenseActivationPanel({
  user,
  systemInfo,
  workspaceMode,
  canSwitch,
  buyerPreviewUx = false,
}) {
  if (!systemInfo || !user) return false;
  if (buyerPreviewUx) return false;
  const designerRole =
    user.is_platform_designer || user.role === "platform_designer";
  if (designerRole && !isSaasCommercialBuyer(user)) return false;
  const subEarly = systemInfo.subscription || {};
  if (subEarly.mode === "platform_designer" && !isSaasCommercialBuyer(user)) return false;

  if (!canInstallLicenseForWorkspace(user)) return false;
  const isDistributor =
    canSwitch && workspaceMode === "distribuidor" && Boolean(user.can_generate_licenses);
  if (isDistributor) return false;

  return subscriptionRequiresLicenseCta(systemInfo);
}
